import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTheme, Text, Tooltip } from '@Sage/hoi-poi-ui';
import {
    CopilotContext,
    CopilotModal,
    COPILOT_CHAT_FEATURES,
    COPILOT_CHAT_INTERACTION_TYPES,
} from '@Sage/ssm-web5';
import useCopilot from 'hooks/useCopilot';
import metadata from 'lib/metadata';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';

import './styles.scss';

const SUPPORTED_FEATURES = [
    COPILOT_CHAT_FEATURES.ACTIVITIES_ANALYZE,
    COPILOT_CHAT_FEATURES.OPPORTUNITIES_ANALYZE,
    COPILOT_CHAT_FEATURES.SALES_ORDERS_ANALYZE,
];

const FEATURE_TOOLTIP_MAP = {
    [COPILOT_CHAT_FEATURES.ACTIVITIES_ANALYZE]: 'helptext_copilot_summary_activity',
    [COPILOT_CHAT_FEATURES.OPPORTUNITIES_ANALYZE]: 'helptext_copilot_summary_opportunities',
    [COPILOT_CHAT_FEATURES.SALES_ORDERS_ANALYZE]: 'helptext_copilot_summary_orders',
};

const FEATURE_EVENT_MAP = {
    [COPILOT_CHAT_FEATURES.ACTIVITIES_ANALYZE]: 'activitiesWidgetSummaryDana',
    [COPILOT_CHAT_FEATURES.OPPORTUNITIES_ANALYZE]: 'opportunitiesWidgetSummaryDana',
    [COPILOT_CHAT_FEATURES.SALES_ORDERS_ANALYZE]: 'salesOrdersWidgetSummaryDana',
};

const MODAL_OVERRIDES = {
    disableChatInput: true,
    disableAgentSwitcher: true,
};

const CopilotLauncher = ({ entity, entityId, feature, name }) => {
    const [isOpen, setIsOpen] = useState(false);

    const theme = useTheme();

    const {
        copilotActivated,
        copilotDisclaimerOnClose,
        copilotActivatedDisclaimer,
        renderCopilotDisclaimer,
        setCopilotModalAfterDisclaimer,
    } = useCopilot({ onConfirmDisclaimerSuccess: () => setIsOpen(true) });

    const { clearChat } = useContext(CopilotContext);

    const chatId = useMemo(() => {
        return `${feature}-${entity.trueName}-${entityId}`;
    }, [entity, entityId, feature]);

    const handleOnClick = useCallback(() => {
        if (copilotActivatedDisclaimer) {
            clearChat({ chatId });
            setIsOpen(true);
            logEvent({
                event: entity.trueName,
                submodule: 'danaAssistant',
                functionality: FEATURE_EVENT_MAP[feature],
            });
        } else {
            setCopilotModalAfterDisclaimer(true);
        }
    }, [
        chatId,
        clearChat,
        copilotActivatedDisclaimer,
        entity,
        feature,
        setCopilotModalAfterDisclaimer,
    ]);

    const handleOnClose = useCallback(() => {
        setIsOpen(false);
        copilotDisclaimerOnClose();
        clearChat({ chatId });
    }, [chatId, clearChat, copilotDisclaimerOnClose]);

    const tooltipContent = useMemo(() => {
        const tooltipKey = FEATURE_TOOLTIP_MAP[feature];
        return tooltipKey ? getLiteral(tooltipKey) : '';
    }, [feature]);

    const interactions = useMemo(
        () => [
            {
                item: {
                    content: { entity: entity.trueName, entityId },
                    type: COPILOT_CHAT_INTERACTION_TYPES.PARAMS,
                },
            },
        ],
        [entity, entityId],
    );

    const session = useMemo(
        () => ({
            features: [feature],
        }),
        [feature],
    );

    const chatSetup = useMemo(
        () => ({
            chatId,
            session,
            interactions,
            overrides: MODAL_OVERRIDES,
        }),
        [chatId, session, interactions],
    );

    const modalTitle = useMemo(
        () => (
            <Text
                color={theme.colors.utility.textSecondary}
            >{`${getLiteral('label_assistant_for')} ${name}`}</Text>
        ),
        [name, theme.colors.utility.textSecondary],
    );

    const modalProps = useMemo(
        () => ({
            chatSetup,
            open: isOpen,
            onClose: handleOnClose,
            title: modalTitle,
        }),
        [chatSetup, isOpen, handleOnClose, modalTitle],
    );

    if (!copilotActivated) return null;

    if (!feature || !SUPPORTED_FEATURES.includes(feature)) {
        console.warn(
            `CopilotLauncher: Unsupported feature "${feature}". Supported features are: ${SUPPORTED_FEATURES.join(', ')}`,
        );
        return null;
    }

    return (
        <>
            <Tooltip content={tooltipContent}>
                <button onClick={handleOnClick} className="fm-button__copilot-launcher">
                    {metadata.SageCopilotIcon}
                </button>
            </Tooltip>
            <CopilotModal {...modalProps} />
            {renderCopilotDisclaimer}
        </>
    );
};

export default memo(CopilotLauncher);
